import jsPDF from "jspdf";

const generatePhishingCertificate = (userData, assessmentData, susanLogo, previewMode = false, exportFormat = 'pdf') => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const centerX = pageWidth / 2;

  // ===== BACKGROUND (WHITE) =====
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 0, pageWidth, pageHeight, "F");

  // ===== CORNERS (dark blue) =====
  doc.setFillColor(6, 28, 49);
  doc.triangle(0, 0, 50, 0, 0, 50, "F");
  doc.triangle(
    pageWidth,
    pageHeight,
    pageWidth - 50,
    pageHeight,
    pageWidth,
    pageHeight - 50,
    "F"
  );

  // ===== BORDERS (dark blue) =====
  doc.setDrawColor(6, 28, 49);
  doc.setLineWidth(1.5);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);
  doc.setLineWidth(0.3);
  doc.rect(13, 13, pageWidth - 26, pageHeight - 26);

  // ===== LOGO =====
  if (susanLogo) {
    try {
      const imgProps = doc.getImageProperties(susanLogo);
      const logoHeight = 25;
      const logoWidth = (imgProps.width * logoHeight) / imgProps.height;
      doc.addImage(
        susanLogo,
        "PNG",
        centerX - logoWidth / 2,
        18,
        logoWidth,
        logoHeight
      );
    } catch {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(6, 28, 49);
      doc.text("SERVQUAL", centerX, 30, { align: "center" });
    }
  } else {
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(6, 28, 49);
    doc.text("SERVQUAL", centerX, 30, { align: "center" });
  }

  // ===== TITLE =====
  const titleY = 60;
  const subTitleY = 68;
  const lineY = 73;
  doc.setFontSize(38);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(6, 28, 49);
  doc.text("CERTIFICATE", centerX, titleY, { align: "center" });
  doc.setFontSize(16);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text("OF ACHIEVEMENT", centerX, subTitleY, { align: "center" });
  doc.setDrawColor(6, 28, 49);
  doc.setLineWidth(0.8);
  doc.line(centerX - 80, lineY, centerX + 80, lineY);
  doc.setFillColor(6, 28, 49);
  doc.circle(centerX - 80, lineY, 1.8, "F");
  doc.circle(centerX, lineY, 1.8, "F");
  doc.circle(centerX + 80, lineY, 1.8, "F");

  // ===== CERT TEXT =====
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "italic");
  doc.text("This is to certify that", centerX, 83, { align: "center" });

  // ===== USER NAME =====
  const userName =
    userData?.name || userData?.["cognito:username"] || "Participant";
  doc.setFontSize(32);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(6, 28, 49);
  doc.text(userName.toUpperCase(), centerX, 96, { align: "center" });
  const nameWidth = doc.getTextWidth(userName.toUpperCase());
  doc.setDrawColor(6, 28, 49);
  doc.setLineWidth(0.5);
  doc.line(centerX - nameWidth / 2 - 8, 98, centerX + nameWidth / 2 + 8, 98);

  // ===== ACHIEVEMENT TEXT =====
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text("has successfully completed the", centerX, 108, {
    align: "center",
  });
  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(6, 28, 49);
  doc.text(
    "Phishing Awareness Training & Assessment",
    centerX,
    117,
    { align: "center" }
  );
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text(
    "and demonstrated excellent knowledge in identifying cyber threats",
    centerX,
    124,
    { align: "center" }
  );

  // ===== SCORE BOX =====
  const boxWidth = 72;
  const boxHeight = 20;
  const boxX = centerX - boxWidth / 2;
  const boxY = lineY + 57;
  doc.setFillColor(51, 161, 72);
  doc.roundedRect(boxX, boxY, boxWidth, boxHeight, 3, 3, "F");
  doc.setDrawColor(6, 28, 49);
  doc.setLineWidth(0.8);
  doc.roundedRect(boxX, boxY, boxWidth, boxHeight, 3, 3, "S");
  const boxCenterY = boxY + boxHeight / 2;
  doc.setFontSize(26);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(
    `${assessmentData.percentage}%`,
    centerX,
    boxCenterY - 1,
    { align: "center", baseline: "middle" }
  );
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(
    "FINAL SCORE",
    centerX,
    boxCenterY + 6,
    { align: "center", baseline: "middle" }
  );

  // ===== DETAILS / ASSESSMENT SUMMARY =====
  const summaryTopGap = 4;
  const detailsY = 160 + summaryTopGap;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text("Assessment Summary", centerX, detailsY - 6, {
    align: "center",
  });
  const columnWidth = 25;
  const columnGap = 20;
  const leftColX = centerX - columnGap / 2 - columnWidth;
  const rightColX = centerX + columnGap / 2;
  const rowGap = 6;
  doc.text(
    `Total Questions: ${assessmentData.totalQuestions}`,
    leftColX,
    detailsY,
    { align: "left" }
  );
  doc.text(
    `Correct Answers: ${assessmentData.correctAnswers}`,
    leftColX,
    detailsY + rowGap,
    { align: "left" }
  );
  doc.text(
    `Attempted: ${assessmentData.answeredQuestions}`,
    rightColX,
    detailsY,
    { align: "left" }
  );
  doc.text(
    `Success Rate: ${Math.round(
      (assessmentData.correctAnswers / assessmentData.totalQuestions) * 100
    )}%`,
    rightColX,
    detailsY + rowGap,
    { align: "left" }
  );

  // ===== DATE =====
  const currentDate = new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "italic");
  doc.text(
    `Date of Completion: ${currentDate}`,
    centerX,
    detailsY + 2 + rowGap + 9,
    { align: "center" }
  );

  // ===== SIGNATURE / ISSUED BY =====
  const signY = 182;
  const rightX = pageWidth - 70;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(0, 0, 0);
  doc.text("Issued by", rightX, signY + 4, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.text("ServQual Limited", rightX, signY + 8, { align: "center" });

  // ===== CERTIFICATE ID =====
  const certificateId = `PHISH-${Date.now()}-${Math.random()
    .toString(36)
    .substr(2, 6)
    .toUpperCase()}`;
  doc.setFontSize(6);
  doc.setTextColor(80, 80, 80);
  doc.setFont("courier", "normal");
  doc.text(`Certificate ID: ${certificateId}`, centerX, 186, {
    align: "center",
  });

  // ===== FOOTER =====
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(6, 28, 49);
  doc.text(
    "ServQual | Cyber Security, Gen AI Risk & Cloud Security Solutions",
    centerX,
    191,
    { align: "center" }
  );
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80, 80, 80);
  doc.setFontSize(6);
  doc.text("https://srql.com", centerX, 195, { align: "center" });

  // ✅ PREVIEW MODE - Return blob URL (DPDP jaisa)
  if (previewMode) {
    const pdfBlob = doc.output('blob');
    return URL.createObjectURL(pdfBlob);
  }

  // ✅ PNG EXPORT MODE
  if (exportFormat === 'png') {
    try {
      const pdfDataUrl = doc.output('dataurlstring');
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);
      
      iframe.onload = function() {
        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
          const canvas = iframeDoc.createElement('canvas');
          const context = canvas.getContext('2d');
          
          canvas.width = 2480;
          canvas.height = 1754;
          
          const img = new Image();
          img.onload = function() {
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
            
            canvas.toBlob(function(blob) {
              if (blob) {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `Phishing_Certificate_${userName.replace(/\s+/g, "_")}_${currentDate.replace(/\s+/g, "_")}.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                console.log('✅ PNG Certificate downloaded successfully!');
              }
              document.body.removeChild(iframe);
            }, 'image/png', 1.0);
          };
          
          img.onerror = function() {
            console.error('❌ Failed to load PDF as image');
            document.body.removeChild(iframe);
            doc.save(`Phishing_Certificate_${userName.replace(/\s+/g, "_")}_${currentDate.replace(/\s+/g, "_")}.pdf`);
          };
          
          img.src = pdfDataUrl;
        } catch (error) {
          console.error('❌ PNG conversion error:', error);
          document.body.removeChild(iframe);
          doc.save(`Phishing_Certificate_${userName.replace(/\s+/g, "_")}_${currentDate.replace(/\s+/g, "_")}.pdf`);
        }
      };
      
      iframe.src = 'about:blank';
    } catch (error) {
      console.error('❌ PNG export failed:', error);
      doc.save(`Phishing_Certificate_${userName.replace(/\s+/g, "_")}_${currentDate.replace(/\s+/g, "_")}.pdf`);
    }
    return 'png_download_initiated';
  }

  // ✅ DEFAULT PDF DOWNLOAD
  const fileName = `Phishing_Certificate_${userName.replace(
    /\s+/g,
    "_"
  )}_${currentDate.replace(/\s+/g, "_")}.pdf`;
  doc.save(fileName);
  return null;
};

export default generatePhishingCertificate;
